import React, { useState } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Separator } from '@/components/ui/separator';
import MobileLeadList from './MobileLeadList';
import DesktopLeadList from './desktop/DesktopLeadList';
import LeadListFilter from './filters/LeadListFilter';
import LeadListHeader from './LeadListHeader';
import { Lead, LeadStatus, LeadOutcome, LeadType } from './types/LeadTypes';
import { useLeadFiltering } from './hooks/useLeadFiltering';
import { getOutcomeCircleColor } from './LeadUtils';

interface LeadListProps {
  leads: Lead[];
  onStatusChange: (id: string, status: LeadStatus) => void;
  onOutcomeChange: (id: string, outcome: LeadOutcome | null) => void;
  onValueChange: (id: string, value: string) => void;
  onNameChange?: (id: string, name: string) => void;
  searchTerm?: string;
  onSearch?: (term: string) => void;
}

const LeadList: React.FC<LeadListProps> = ({
  leads,
  onStatusChange,
  onOutcomeChange,
  onValueChange,
  onNameChange = () => {}, // Default empty function if not provided
  searchTerm = '',
  onSearch
}) => {
  const isMobile = useIsMobile();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  
  // Remove timeFrame from useLeadFiltering
  const {
    localSearchTerm,
    setLocalSearchTerm,
    filters,
    setFilters,
    hasActiveFilters,
    filteredLeads
  } = useLeadFiltering({ 
    leads,
    searchTerm
  });
  
  // Remove timeFrame debug logs
  // ... existing code ...

  const handleSearch = (term: string) => {
    setLocalSearchTerm(term);
    if (onSearch) {
      onSearch(term);
    }
  };

  return (
    <div className="w-full">
      <div className={`sticky top-[118px] md:top-[118px] z-30 bg-brand-light pb-3 pt-3 w-full ${isMobile ? 'border-b border-gray-200 shadow-sm' : ''}`}>
        <LeadListHeader
          isSearchOpen={isSearchOpen}
          isFilterOpen={isFilterOpen}
          localSearchTerm={localSearchTerm}
          setIsSearchOpen={setIsSearchOpen}
          setIsFilterOpen={setIsFilterOpen}
          handleSearch={handleSearch}
          filters={filters}
          hasActiveFilters={hasActiveFilters}
        />
        
        {isFilterOpen && (
          <div className={`mt-3 px-4 md:px-6 w-full ${isMobile ? '' : 'border-0'}`}>
            <LeadListFilter 
              filters={filters} 
              onFiltersChange={setFilters} 
              onClose={() => setIsFilterOpen(false)} 
            />
          </div>
        )}
      </div>
      
      {isFilterOpen && !isMobile && <div className="h-4"></div>}
      
      <div className={`bg-white ${isMobile ? 'border-x border-b' : 'rounded-lg border border-gray-200'} shadow-sm w-full`}>
        {isMobile ? (
          <MobileLeadList 
            leads={filteredLeads} 
            onStatusChange={onStatusChange} 
            onOutcomeChange={onOutcomeChange} 
            onValueChange={onValueChange} 
            onNameChange={onNameChange}
          />
        ) : (
          <DesktopLeadList 
            leads={filteredLeads} 
            onStatusChange={onStatusChange} 
            onOutcomeChange={onOutcomeChange} 
            onValueChange={onValueChange} 
            onNameChange={onNameChange}
          />
        )}
      </div>
    </div>
  );
};

// Export types
export type { Lead, LeadStatus, LeadOutcome, LeadType };
export { getOutcomeCircleColor };

export default LeadList;
